import React from "react";
import { useQuery } from "@apollo/react-hooks";
import { gql } from "apollo-boost";

import SettingsList from "superAdminViews/setting/SettingsList";

const ADD = gql`
  mutation createCarModelByAdmin($name: String!, $brand: ID!) {
    createCarModelByAdmin(input: { name: $name, brand: $brand }) {
      _id
      name
    }
  }
`;

const GET = gql`
  query getCarModels($limit: Int, $skip: Int, $name: String, $brand: ID) {
    getCarModels(
      pagination: { limit: $limit, skip: $skip }
      filters: { name: $name, brand: $brand }
    ) {
      _id
      name
      brand {
        _id
        name
      }
    }
  }
`;

const COUNT = gql`
  query getCarModelsCount($name: String, $brand: ID) {
    getCarModelsCount(filters: { name: $name, brand: $brand })
  }
`;

const UPDATE = gql`
  mutation updateCarModelByAdmin($id: ID!, $name: String!, $brand: ID!) {
    updateCarModelByAdmin(id: $id, input: { name: $name, brand: $brand }) {
      _id
      name
      brand {
        _id
        name
      }
    }
  }
`;

const DELETE = gql`
  mutation deleteCarModelByAdmin($idSet: [ID!]!) {
    deleteCarModelByAdmin(idSet: $idSet) {
      _id
    }
  }
`;

const IMPORT = gql`
  mutation addCarModelViaExcel($excelFile: Upload!) {
    addCarModelViaExcel(excelFile: $excelFile)
  }
`;

const GET_BRANDS = gql`
  query getCarBrands {
    getCarBrands {
      _id
      name
    }
  }
`;

/**
 * Models - the models under each car brand.
 *
 * The brand filter existed in the query but had no control on the
 * screen, so a list of every model across every make was the only view
 * available.
 */
const Models = () => {
  const { data } = useQuery(GET_BRANDS);
  const brands = (data && data.getCarBrands) || [];
  const brandOptions = brands.map((brand) => ({ value: brand._id, label: brand.name }));

  return (
    <SettingsList
      title="Models"
      description="The models available under each brand."
      singular="model"
      list={{ query: GET, key: "getCarModels" }}
      count={{ query: COUNT, key: "getCarModelsCount" }}
      searchFilter="name"
      filters={[{ name: "brand", label: "Brand", options: brandOptions }]}
      create={{
        mutation: ADD,
        variables: (values) => ({ name: values.name.trim(), brand: values.brand }),
      }}
      update={{
        mutation: UPDATE,
        variables: (values, row) => ({
          id: row._id,
          name: values.name.trim(),
          brand: values.brand,
        }),
      }}
      remove={{ mutation: DELETE }}
      importExcel={{
        mutation: IMPORT,
        templateRows: [
          ["Brand", "Name"],
          ["Benz", "C-Class"],
          ["Mazda", "CX-5"],
        ],
        fileName: "Spark-Models-Template.xlsx",
      }}
      columns={[
        { key: "name", label: "Model", width: "2fr" },
        {
          key: "brand",
          label: "Brand",
          width: "2fr",
          render: (row) => (row.brand ? row.brand.name : "—"),
        },
      ]}
      fields={[
        { name: "name", label: "Model", type: "text", required: true },
        {
          name: "brand",
          label: "Brand",
          type: "select",
          required: true,
          options: brandOptions,
        },
      ]}
      toValues={(row) => ({ name: row.name, brand: row.brand ? row.brand._id : "" })}
    />
  );
};

export default Models;
